import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import type { GlobalCommandDefinition } from '@luminovo/data-stack';
import { useNavigate } from '@luminovo/design-system';
import { deriveTopLevelAppRoutes, isAddressable, useSlotAssignments } from '@luminovo/slots';
import { AppWindowIcon } from 'lucide-react';
import React, { useMemo } from 'react';

import { fullPageAppLink } from '../fullPageAppLink';

export function useFullPageSlotCommands(): GlobalCommandDefinition[] {
    const navigate = useNavigate();
    const { i18n } = useLingui();
    const { data } = useSlotAssignments('slots.full-page');
    const assignments = data?.assignments ?? [];

    return useMemo(
        () =>
            deriveTopLevelAppRoutes(assignments.filter(isAddressable), i18n.locale).flatMap(({ app, source, routes }) =>
                routes.map(({ path, label }): GlobalCommandDefinition => {
                    const commandLabel = source === 'derived' && routes.length === 1 ? app.name : label;
                    return {
                        id: `full-page-slot-${app.id}-${path}`,
                        label: () => commandLabel,
                        description: () => app.description || app.name,
                        group: () => t`Apps`,
                        icon: React.createElement(AppWindowIcon, { size: 16 }),
                        keywords: ['app', 'apps', app.name, label],
                        onSelect: (context) => {
                            navigate({ ...fullPageAppLink(path), openInNewTab: context.openInNewTab });
                            context.close();
                        },
                        closeOnComplete: true,
                    };
                }),
            ),
        [assignments, i18n.locale, navigate],
    );
}
